/**
 * ResultsTable Component
 * Displays SERP ranking results in a comprehensive table
 * Supports both single and multiple keyword results
 */
import React, { useState } from 'react';

const ResultsTable = ({ result }) => {
  const [selectedKeyword, setSelectedKeyword] = useState(0);

  if (!result) return null;

  const { domain, timestamp, total_keywords, results } = result;
  
  // Check if this is a multiple keywords result
  const isMultipleKeywords = total_keywords && total_keywords > 1 && results;
  
  // Get current result to display
  const currentResult = isMultipleKeywords ? results[selectedKeyword] : result;
  const { keyword, position, found, url, title, snippet, top_results } = currentResult;

  /**
   * Format timestamp to readable date
   */
  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  /**
   * Truncate text with ellipsis
   */
  const truncate = (text, maxLength = 150) => {
    if (!text) return 'N/A';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  return (
    <div className="space-y-6">
      {/* Multiple Keywords Navigation */}
      {isMultipleKeywords && (
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">
            Multiple Keywords Results ({total_keywords} keywords)
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {results.map((res, index) => (
              <button
                key={index}
                onClick={() => setSelectedKeyword(index)}
                className={`p-4 rounded-lg border-2 transition-all text-left ${
                  selectedKeyword === index
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-gray-200 hover:border-gray-300 bg-white'
                }`}
              >
                <p className="text-sm font-medium text-gray-700 mb-1 truncate" title={res.keyword}>
                  {res.keyword}
                </p>
                {res.found ? (
                  <p className="text-2xl font-bold text-primary-600">#{res.position}</p>
                ) : (
                  <p className="text-sm text-red-600 font-medium">Not Found</p>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Main Result Card */}
      <div className="card">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-xl font-bold text-gray-900 mb-1">
              {isMultipleKeywords ? `Keyword ${selectedKeyword + 1} Result` : 'Ranking Result'}
            </h3>
            <p className="text-sm text-gray-500">
              Checked on {formatDate(timestamp)}
            </p>
          </div>
          
          {/* Status Badge */}
          {found ? (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
              <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              Found
            </span>
          ) : (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
              <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              Not Found
            </span>
          )}
        </div>

        {/* Summary Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600 mb-1">Keyword</p>
            <p className="font-semibold text-gray-900">{keyword}</p>
          </div>
          
          <div className="p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600 mb-1">Domain</p>
            <p className="font-semibold text-gray-900">{domain}</p>
          </div>
          
          <div className="p-4 bg-primary-50 rounded-lg">
            <p className="text-sm text-gray-600 mb-1">Position</p>
            <p className="font-bold text-2xl text-primary-600">
              {found ? `#${position}` : 'Not in Top 50'}
            </p>
          </div>
        </div>

        {/* Found Result Details */}
        {found && (
          <div className="border-t pt-4">
            <h4 className="font-semibold text-gray-900 mb-3">Your Ranking Entry:</h4>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <a 
                href={url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-primary-600 hover:text-primary-800 font-medium hover:underline mb-2 block"
              >
                {title}
              </a>
              <p className="text-sm text-gray-600 mb-2">{truncate(snippet)}</p>
              <p className="text-xs text-gray-500 break-all">{url}</p>
            </div>
          </div>
        )}
      </div>

      {/* Top 10 Results Table */}
      {top_results && top_results.length > 0 && (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-gray-900">
              Top 10 Search Results
            </h3>
            <span className="text-sm text-gray-500">
              {top_results.length} results
            </span>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                    Rank
                  </th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Title
                  </th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">
                    Snippet
                  </th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                    URL
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {top_results.map((result, index) => {
                  const isTarget = found && result.position === position;
                  return (
                    <tr 
                      key={index} 
                      className={`hover:bg-gray-50 transition-colors ${isTarget ? 'bg-blue-50 border-l-4 border-primary-500' : ''}`}
                    >
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-semibold ${
                          isTarget 
                            ? 'bg-primary-600 text-white' 
                            : 'bg-gray-100 text-gray-700'
                        }`}>
                          {result.position}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <a 
                          href={result.link} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-primary-600 hover:text-primary-800 hover:underline font-medium"
                        >
                          {truncate(result.title, 60)}
                        </a>
                        {isTarget && (
                          <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-800">
                            Your Site
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-600 hidden lg:table-cell">
                        {truncate(result.snippet, 100)}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-500 hidden md:table-cell">
                        <span className="truncate block max-w-xs" title={result.link}>
                          {result.displayed_link || new URL(result.link).hostname}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResultsTable;
